'use client';

import { useAuthSessionHint } from '@/hooks/auth/use-has-auth-session-hint';
import { authClient } from '@/lib/auth/auth-client';
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

type BetterAuthSessionState = ReturnType<(typeof authClient)['useSession']>;

export type AuthSessionState = Pick<
  BetterAuthSessionState,
  'data' | 'error' | 'isPending' | 'isRefetching'
> & {
  refetch: () => Promise<void>;
};

type AuthSessionSnapshot = Omit<AuthSessionState, 'refetch'>;

type AuthSessionReadControl = {
  hasStarted: boolean;
  requestRead: () => void;
};

type InitialReadWaiter = {
  observedPending: boolean;
  resolve: () => void;
};

const IDLE_SESSION_SNAPSHOT: AuthSessionSnapshot = {
  data: null,
  error: null,
  isPending: false,
  isRefetching: false,
};

const INITIAL_READER_SESSION_SNAPSHOT: AuthSessionSnapshot = {
  data: null,
  error: null,
  isPending: true,
  isRefetching: false,
};

const AuthSessionContext = createContext<AuthSessionState | null>(null);
const AuthSessionReadControlContext =
  createContext<AuthSessionReadControl | null>(null);

export function AuthSessionProvider({ children }: { children: ReactNode }) {
  const { hasAuthSessionHint, isResolved: isAuthSessionHintResolved } =
    useAuthSessionHint();
  const [hasStartedReader, setHasStartedReader] = useState(false);
  const [sessionSnapshot, setSessionSnapshot] = useState<AuthSessionSnapshot>(
    INITIAL_READER_SESSION_SNAPSHOT
  );
  const readerRefetchRef = useRef<BetterAuthSessionState['refetch'] | null>(
    null
  );
  const initialReadWaitersRef = useRef<InitialReadWaiter[]>([]);

  const requestRead = useCallback(() => {
    // Once mounted, keep the sole Better Auth subscriber alive for the whole
    // Providers lifetime. Unmounting it on hint/consumer churn aborts the
    // in-flight request and makes the next subscriber start another one.
    setHasStartedReader(true);
  }, []);

  useEffect(() => {
    if (hasAuthSessionHint) {
      requestRead();
    }
  }, [hasAuthSessionHint, requestRead]);

  useEffect(() => {
    return () => {
      const waiters = initialReadWaitersRef.current;
      initialReadWaitersRef.current = [];
      for (const waiter of waiters) {
        waiter.resolve();
      }
    };
  }, []);

  const refetch = useCallback<AuthSessionState['refetch']>(() => {
    const readerRefetch = readerRefetchRef.current;
    if (readerRefetch) {
      return readerRefetch();
    }

    requestRead();
    return new Promise<void>((resolve) => {
      initialReadWaitersRef.current.push({
        observedPending: false,
        resolve,
      });
    });
  }, [requestRead]);

  const registerReaderRefetch = useCallback(
    (readerRefetch: BetterAuthSessionState['refetch']) => {
      readerRefetchRef.current = readerRefetch;
      return () => {
        if (readerRefetchRef.current === readerRefetch) {
          readerRefetchRef.current = null;
        }
      };
    },
    []
  );

  const publishReaderSnapshot = useCallback(
    (nextSnapshot: AuthSessionSnapshot) => {
      setSessionSnapshot(nextSnapshot);

      const isBusy = nextSnapshot.isPending || nextSnapshot.isRefetching;
      const waiters = initialReadWaitersRef.current;
      if (waiters.length === 0) {
        return;
      }

      if (isBusy) {
        for (const waiter of waiters) {
          waiter.observedPending = true;
        }
        return;
      }

      const completedWaiters = waiters.filter(
        (waiter) => waiter.observedPending
      );
      if (completedWaiters.length === 0) {
        return;
      }

      initialReadWaitersRef.current = waiters.filter(
        (waiter) => !waiter.observedPending
      );
      for (const waiter of completedWaiters) {
        waiter.resolve();
      }
    },
    []
  );

  const visibleSnapshot = hasStartedReader
    ? sessionSnapshot
    : isAuthSessionHintResolved
      ? IDLE_SESSION_SNAPSHOT
      : INITIAL_READER_SESSION_SNAPSHOT;
  const sessionValue = useMemo<AuthSessionState>(
    () => ({
      ...visibleSnapshot,
      refetch,
    }),
    [refetch, visibleSnapshot]
  );
  const readControlValue = useMemo<AuthSessionReadControl>(
    () => ({
      hasStarted: hasStartedReader,
      requestRead,
    }),
    [hasStartedReader, requestRead]
  );

  return (
    <AuthSessionReadControlContext.Provider value={readControlValue}>
      <AuthSessionContext.Provider value={sessionValue}>
        {children}
        {hasStartedReader ? (
          <AuthSessionReader
            onRefetchAvailable={registerReaderRefetch}
            onSnapshotChange={publishReaderSnapshot}
          />
        ) : null}
      </AuthSessionContext.Provider>
    </AuthSessionReadControlContext.Provider>
  );
}

function AuthSessionReader({
  onRefetchAvailable,
  onSnapshotChange,
}: {
  onRefetchAvailable: (
    refetch: BetterAuthSessionState['refetch']
  ) => () => void;
  onSnapshotChange: (snapshot: AuthSessionSnapshot) => void;
}) {
  const { data, error, isPending, isRefetching, refetch } =
    authClient.useSession();

  useEffect(() => onRefetchAvailable(refetch), [onRefetchAvailable, refetch]);

  useEffect(() => {
    onSnapshotChange({
      data,
      error,
      isPending,
      isRefetching,
    });
  }, [data, error, isPending, isRefetching, onSnapshotChange]);

  return null;
}

export function useAuthSession(): AuthSessionState {
  const session = useContext(AuthSessionContext);
  if (!session) {
    throw new Error('useAuthSession must be used within AuthSessionProvider.');
  }
  return session;
}

export function useAuthSessionReadRequest(enabled: boolean) {
  const control = useContext(AuthSessionReadControlContext);
  if (!control) {
    throw new Error(
      'useAuthSessionReadRequest must be used within AuthSessionProvider.'
    );
  }

  const { hasStarted, requestRead } = control;
  useEffect(() => {
    if (enabled) {
      requestRead();
    }
  }, [enabled, requestRead]);

  return hasStarted;
}
